import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ActivityLoggerService } from 'src/app/_shared/service/activity-logger.service';
import constant from 'src/constant.json';
import { LoanComputationService } from '../../service/loan-computation.service';
import { MyHomeState } from '../../state/my-home.state';

@Component({
  selector: 'app-refinance-mortgage-scenario',
  templateUrl: './refinance-mortgage-scenario.component.html',
  styleUrls: ['./refinance-mortgage-scenario.component.scss'],
})
export class RefinanceMortgageScenarioComponent implements OnInit, OnChanges {
  constructor(
    private loanComputationService: LoanComputationService,
    private formBuilder: FormBuilder,
    private decimalPipe: DecimalPipe,
    private activityLoggerService: ActivityLoggerService,
    private myHomeState: MyHomeState
  ) { }
  @Input() principalBalance;
  @Input() termBalance;
  @Input() interestRate;
  @Input() isCurrentMortgage;
  @Input() isActive;
  @Input() computedLoan;
  @Input() heading;
  @Input() loanId;
  @Input() isDelete;
  @Input() term;
  @Input() closingCost;

  private sliderValue = 0;
  private sliderValue2 = 0;
  private debounceSubject = new Subject<number>();
  onDestroyNotifier$ = new Subject();

  computedLoanValue;
  closeByYears;
  closeByMonths;
  loanInterest;
  defaultLoanInterestRate;
  loanComputedLoan;
  loanTotalSavings;
  loanMonthlyPayment = 0;
  changeInMonthlyPayment = 0;
  errorInField: any = {};
  totalInterestChange;
  isCurrentYears;
  isCurrentMonths;
  timeBreakEvent = 0;
  closeByYearsInputValid;
  interestRateInputValid;
  loggerAddress: any;
  pmxPropertyId;

  loanRefinanceForm = new FormGroup({});
  totalInterestSaved;
  @Output() changeInLoanValue = new EventEmitter<any>();

  ngOnInit() {
    this.myHomeState.activePropertyDetails$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res && res.basic) {
        this.pmxPropertyId = res.basic.PMXPropertyId;

        this.loggerAddress = {
          city: res.basic.City,
          state: res.basic.StateOrProvince,
          street: res.basic.StreetAddress,
          zip: res.basic.PostalCode,
        };
      }
    });
    this.debounceSubject.pipe(debounceTime(500)).subscribe(() => {
      this.logUserActivity(
        constant.LOGGER.myHome.category,
        constant.LOGGER.myHome.action.editLoanscenario.key,
        this.pmxPropertyId,
        this.loggerAddress
      );
    });
  }

  closeYears;
  ngOnChanges() {
    this.loanRefinanceForm = this.formBuilder.group({
      closeByYearsInput: ['', [Validators.required, Validators.min(0), Validators.max(30)]],
      closeByYearsSlider: [''],
      interestRateInput: ['', [Validators.required, Validators.min(0), Validators.min(15)]],
      interestRateSlider: ['', []],
      closingCostInput: [0, [Validators.required]],
      // closingCostSlider: ['', []],
    });
    if (this.isCurrentMortgage) {
      this.isCurrentYears = Math.floor(this.computedLoan.termBalance / 12);
      this.isCurrentMonths = this.computedLoan.termBalance % 12;
    }
    this.closeByYears = this.termBalance ? Math.floor(this.termBalance / 12) : Math.floor(this.termBalance / 12);
    this.closeByMonths = this.termBalance ? Math.round(this.termBalance % 12) : Math.round(this.termBalance % 12);
    this.closeYears = this.decimalPipe.transform(this.closeByYears + this.closeByMonths / 100, '1.2-2');
    this.loanInterest = this.interestRate ? this.interestRate : 0;
    this.loanRefinanceForm.patchValue({
      closeByYearsInput: this.closeByYears,
      closeByYearsSlider: this.closeByYears,
      interestRateInput: this.loanInterest,
      interestRateSlider: this.loanInterest,
      closingCostInput: this.closingCost,
      // closingCostSlider: this.closingCost,
    });

    this.loanComputedLoan = this.loanComputationService.computeBalanceLoan(
      this.principalBalance,
      this.loanInterest / 100,
      this.closeByYears * 12 + this.closeByMonths
    );

    this.loanMonthlyPayment = this.loanComputedLoan?.paymentMonthly;
    this.changeInMonthlyPayment = this.loanComputedLoan?.paymentMonthly - this.computedLoan?.paymentMonthly;
    if (this.changeInMonthlyPayment > 0) this.timeBreakEvent = 0;

    this.totalInterestChange =
      this.computedLoan?.interestBalance / this.computedLoan?.termBalance -
      this.loanComputedLoan?.interestBalance / (this.closeByYears * 12 + this.closeByMonths);

    this.totalInterestSaved = this.totalInterestChange * (this.closeByYears * 12 + this.closeByMonths);
    if (this.closingCost && this.changeInMonthlyPayment < 0) {
      this.timeBreakEvent = Math.ceil(this.closingCost / this.changeInMonthlyPayment);
    }

    this.loanRefinanceForm
      .get('closeByYearsInput')
      .valueChanges.pipe(debounceTime(600))
      .subscribe((value) => {
        if (value >= 1 && value < 31) {
          this.closeByYearsInputValid = false;
          this.calculateLoan('closeByYearsInput', value);
          this.loanRefinanceForm.patchValue({ closeByYearsSlider: value }, { emitEvent: false });
        } else {
          this.closeByYearsInputValid = true;
        }
      });
    this.loanRefinanceForm
      .get('closeByYearsSlider')
      .valueChanges.pipe()
      .subscribe((value) => {
        this.loanRefinanceForm.patchValue({ closeByYearsInput: value }, { emitEvent: true });
      });
    this.loanRefinanceForm
      .get('interestRateInput')
      .valueChanges.pipe(debounceTime(600))
      .subscribe((value) => {
        if (value >= 1 && value < 16) {
          this.interestRateInputValid = false;
          this.calculateLoan('interestRateInput', value);
          this.loanRefinanceForm.patchValue({ interestRateSlider: value }, { emitEvent: false });
        } else {
          this.interestRateInputValid = true;
        }
      });
    this.loanRefinanceForm
      .get('interestRateSlider')
      .valueChanges.pipe()
      .subscribe((value) => {
        this.loanRefinanceForm.patchValue({ interestRateInput: value }, { emitEvent: true });
      });
    this.loanRefinanceForm
      .get('closingCostInput')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((value) => {
        if (value >= 0) {
          this.calculateLoan('closingCostInput', value);
          // this.loanRefinanceForm.patchValue({ closingCostSlider: value }, { emitEvent: false });
        }
      });
    // this.loanRefinanceForm
    //   .get('closingCostSlider')
    //   .valueChanges.pipe()
    //   .subscribe((value) => {
    //     this.loanRefinanceForm.patchValue({ closingCostInput: value }, { emitEvent: true });
    //   });
  }

  onSliderChange(event: any) {
    this.sliderValue = event.value;
    this.debounceSubject.next(this.sliderValue);
  }

  logUserActivity(category, action, pmxPropertyId, address) {
    let loggerParams = {
      category: category,
      action: action,
    };
    let inputData = {
      pmxPropertyId: pmxPropertyId,
      address: address,
    };
    this.activityLoggerService.logActivity(loggerParams, inputData);
  }
  calculateLoan(fieldName?, value?) {
    this.computedLoanValue = this.loanComputationService.computeBalanceLoan(
      this.principalBalance,
      this.loanRefinanceForm.value.interestRateInput / 100,
      this.loanRefinanceForm.value.closeByYearsInput * 12 + this.closeByMonths
    );
    if (this.computedLoanValue) {
      this.loanMonthlyPayment = this.computedLoanValue.paymentMonthly;
      this.changeInMonthlyPayment = this.computedLoanValue.paymentMonthly - this.computedLoan.paymentMonthly;
      if (this.changeInMonthlyPayment < 0) {
        this.timeBreakEvent = Math.ceil(this.loanRefinanceForm.value.closingCostInput / this.changeInMonthlyPayment);
      }
      if (this.changeInMonthlyPayment > 0) {
        this.timeBreakEvent = 0;
      }
      this.totalInterestChange =
        this.computedLoan.interestBalance / (this.closeByYears * 12 + this.closeByMonths) -
        this.loanComputedLoan.interestBalance / (this.closeByYears * 12 + this.closeByMonths);
      //  this.term1TotalSavings = this.computedLoan.totalBalance - this.term1ComputedLoan.totalBalance;
    }
    this.updateRefinanceLoanValue(fieldName, value, this.loanId);
  }

  updateRefinanceLoanValue(key, value, loanId) {
    let refinanceParams = {
      loanId: loanId,
      updatekey: key,
      changeValue: value,
      option: 'update',
    };
    if (value && key) {
      this.changeInLoanValue.emit(refinanceParams);
    }
  }

  selectedLoanAction(loanId, option) {
    let value = {
      loanId: loanId || 0,
      option: option,
    };
    this.changeInLoanValue.emit(value);
  }

  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }
}
